import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CAROUSEL_IMAGES = [
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200',
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200',
  'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200',
];

const CAROUSEL_INTERVAL = 5000;

const bulletPoints = [
  'Audit de conformité RH automatisé',
  'Génération de documents juridiques par IA',
  'Suivi des obligations légales en temps réel',
];

interface AuthShellProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Habillage commun pour les écrans d'authentification Clerk.
 * Le panneau droit laisse Clerk gérer sa propre carte (pas de double cadre).
 */
const AuthShell: React.FC<AuthShellProps> = ({ children, title, subtitle }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, CAROUSEL_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'auto',
        background: '#f8f9fb',
      }}
    >
      {isMobile && (
        <div
          style={{
            background: '#1B2A4A',
            padding: '2rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1.25rem',
              }}
            >
              J
            </div>
            <span style={{ fontWeight: 700, fontSize: '1.35rem' }}>
              JU&apos;LIA <span style={{ color: '#4ECDC4', fontSize: '0.9rem' }}>IA</span>
            </span>
          </div>
          {title && <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>{title}</p>}
        </div>
      )}

      {!isMobile && (
        <div
          style={{
            flex: 1,
            position: 'relative',
            minHeight: '100vh',
            overflow: 'hidden',
            color: '#fff',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${CAROUSEL_IMAGES[currentImage]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </AnimatePresence>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(27,42,74,0.92) 0%, rgba(45,95,138,0.85) 100%)',
            }}
          />
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: isTablet ? '2.5rem' : '4rem',
            }}
          >
            <div style={{ maxWidth: 480 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2.5rem' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1.5rem',
                  }}
                >
                  J
                </div>
                <span style={{ fontWeight: 700, fontSize: '1.75rem' }}>
                  JU&apos;LIA <span style={{ color: '#4ECDC4', fontSize: '1rem' }}>IA</span>
                </span>
              </div>
              <h1
                style={{
                  fontSize: isTablet ? '1.85rem' : '2.5rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: '1.25rem',
                }}
              >
                <span style={{ color: '#4ECDC4' }}>Votre plateforme RH</span>
                <br />
                <span style={{ color: '#4ECDC4' }}>intelligente</span>
              </h1>
              <p style={{ fontSize: '1.05rem', opacity: 0.9, lineHeight: 1.7, maxWidth: 400 }}>
                Simplifiez la gestion de vos ressources humaines, automatisez vos obligations
                légales et prenez soin de vos collaborateurs.
              </p>
              <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bulletPoints.map((item) => (
                  <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#4ECDC4',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '0.95rem' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          width: isMobile ? '100%' : isTablet ? 440 : 520,
          flexShrink: 0,
          minHeight: isMobile ? 'auto' : '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '1.5rem 1rem 2rem' : '2.5rem 1.5rem',
          background: '#f8f9fb',
        }}
      >
        {(title || subtitle) && !isMobile && (
          <div style={{ width: '100%', maxWidth: 420, marginBottom: '1rem', textAlign: 'left' }}>
            {title && (
              <h2
                style={{
                  margin: '0 0 0.35rem',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const clerkAppearance = {
  variables: {
    colorPrimary: '#2D5F8A',
    colorText: '#1B2A4A',
    borderRadius: '0.75rem',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  elements: {
    rootBox: {
      width: '100%',
      maxWidth: '420px',
    },
    card: {
      width: '100%',
      maxWidth: '420px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: '1rem',
      margin: '0 auto',
    },
    cardBox: {
      width: '100%',
      maxWidth: '420px',
      boxShadow: 'none',
    },
    main: {
      width: '100%',
    },
    formButtonPrimary: {
      backgroundColor: '#4ECDC4',
      fontSize: '0.95rem',
      fontWeight: '600',
      boxShadow: '0 4px 14px rgba(78,205,196,0.35)',
    },
    footerActionLink: {
      color: '#2D5F8A',
      fontWeight: '600',
    },
  },
};

export default AuthShell;
